import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties } from 'react';
import { deriveInitials } from '../lib/initials';
import { getAvatarDirectory, type AvatarDirectory } from '../lib/api';

/** A circular user badge: the uploaded avatar image when `url` is set, otherwise the
 *  person's initials. One component for every place we show a person (sidebar, profile,
 *  board cards, comments, state-flow) so the image/initials fallback stays identical.
 *  `size` is the diameter in px (the shared `.profile-avatar` styling supplies colour,
 *  shape and centring; we only override the dimensions + font here). */
export function Avatar({
  name, firstName, lastName, initials, url, size = 32, fontSize, className, title, style,
}: {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Explicit initials override (e.g. the user's chosen `initials`); else derived. */
  initials?: string | null;
  /** Resolved avatar URL (caller resolves via `resolveAvatar`); falsy → initials. */
  url?: string | null;
  size?: number;
  fontSize?: number;
  className?: string;
  title?: string;
  style?: CSSProperties;
}) {
  const glyphs = initials?.trim() || deriveInitials(name ?? '', firstName ?? '', lastName ?? '');
  // If the image URL fails to load (e.g. a 404 from a stale directory entry after the
  // avatar was removed, or missing bytes on disk), degrade to initials — that's this
  // component's whole contract. Reset the flag whenever the URL changes.
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  return (
    <span
      className={`profile-avatar${className ? ` ${className}` : ''}`}
      title={title}
      style={{ width: size, height: size, minWidth: size, fontSize: fontSize ?? Math.round(size * 0.42), ...style }}
    >
      {url && !failed
        ? <img className="profile-avatar-img" src={url} alt="" draggable={false} loading="lazy" decoding="async" onError={() => setFailed(true)} />
        : <span aria-hidden>{glyphs}</span>}
    </span>
  );
}

const EMPTY: AvatarDirectory = { byEmail: {}, byName: {} };

/** Shared name/email → avatar-URL maps (cached 5 min). Call once per component that
 *  renders many avatars (e.g. the board) and pass the result to `resolveAvatar`. */
export function useAvatarDirectory(): AvatarDirectory {
  const { data } = useQuery({ queryKey: ['avatar-directory'], queryFn: getAvatarDirectory, staleTime: 5 * 60_000 });
  return data ?? EMPTY;
}

/** Resolve a person (known by ADO email and/or display name) to an avatar URL.
 *  Email wins over name (more reliable); returns undefined → the caller shows initials. */
export function resolveAvatar(dir: AvatarDirectory | undefined, opts: { email?: string | null; name?: string | null }): string | undefined {
  if (!dir) return undefined;
  const e = opts.email?.trim().toLowerCase();
  if (e && dir.byEmail[e]) return dir.byEmail[e];
  const n = opts.name?.trim().toLowerCase();
  if (n && dir.byName[n]) return dir.byName[n];
  return undefined;
}
